import React, { useState, useEffect } from 'react'
import api from '../services/api'
import { simpananBerjangkaService } from '../services/simpananBerjangkaService'
import '../styles/dashboard.css'
import { showSuccess, showError, showWarning, showInfo, showConfirm } from '../utils/alert';


function Dashboard() {
  const [stats, setStats] = useState({
    totalAnggota: 0,
    anggotaAktif: 0,
    totalSimpanan: 0,
    totalPinjaman: 0,
    pinjamanAktif: 0,
    sisaPinjaman: 0,
    transaksiHarian: 0
  })
  const [loading, setLoading] = useState(true)
  const [todayTransactions, setTodayTransactions] = useState([])
  const [recentLoans, setRecentLoans] = useState([])
  const [uangPangkalBelumLunas, setUangPangkalBelumLunas] = useState([])
  const [calculatingInterest, setCalculatingInterest] = useState(false)
  const [jatuhTempo, setJatuhTempo] = useState({
    overdue: [],
    dueToday: [],
    dueSoon: [],
    summary: { overdue: 0, dueToday: 0, dueSoon: 0 }
  })

  useEffect(() => {
    fetchDashboardData()
    fetchJatuhTempoWarnings()
  }, [])

  const handleCalculateInterest = async () => {
    // Menggunakan showConfirm (asynchronous)
    const confirmed = await showConfirm(
      'Konfirmasi Pembungaan', 
      'Apakah Anda yakin ingin melakukan pembungaan harian simpanan sukarela?'
    );

    if (!confirmed) {
      return;
    }

    setCalculatingInterest(true);
    try {
      const response = await api.post('/savings/pembungaan');
      const data = response.data.data || response.data;
      
      showInfo(
        `Pembungaan berhasil diproses:\n` +
        `- ${data.processed} rekening diproses\n` +
        `- Total bunga: Rp ${(data.totalInterest || 0).toLocaleString('id-ID')}`
      );
      
      fetchDashboardData();
    } catch (error) {
      showWarning(
        'Gagal melakukan pembungaan: ' + 
        (error.response?.data?.message || error.message)
      );
    } finally {
      setCalculatingInterest(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard/stats')
      const data = response.data.data || {}

      setStats({
        totalAnggota: data.totalAnggota || 0,
        anggotaAktif: data.anggotaAktif || 0,
        totalSimpanan: data.totalSimpanan || 0,
        totalPinjaman: data.totalPinjaman || 0,
        pinjamanAktif: data.pinjamanAktif || 0,
        sisaPinjaman: data.sisaPinjaman || 0,
        transaksiHarian: data.transaksiHarian || 0
      })

      setTodayTransactions(data.todayTransactions || [])
      setRecentLoans(data.recentLoans || [])
      setUangPangkalBelumLunas(data.uangPangkalBelumLunas || [])
    } catch (error) {
      console.error('Error fetching dashboard:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchJatuhTempoWarnings = async () => {
    try {
      const res = await simpananBerjangkaService.getJatuhTempoWarnings()
      const data = res.data?.data || {}
      setJatuhTempo({
        overdue: data.overdue || [],
        dueToday: data.dueToday || [],
        dueSoon: data.dueSoon || [],
        summary: data.summary || { overdue: 0, dueToday: 0, dueSoon: 0 }
      })
    } catch (error) {
      console.error('Error fetching jatuh tempo warnings:', error)
    }
  }

  const formatRupiah = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Memuat dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="dashboard-container">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="text-white mb-0">Dashboard</h2>
              <p className="text-white-50 mb-0">Selamat datang di Sistem Koperasi</p>
            </div>
            <div className="text-white text-right">
              <button
                className="btn btn-info btn-sm mb-2"
                onClick={handleCalculateInterest}
                disabled={calculatingInterest}
              >
                {calculatingInterest ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2"></span>
                    Memproses...
                  </>
                ) : (
                  <>
                    <i className="fas fa-calculator mr-2"></i>
                    Pembungaan Harian
                  </>
                )}
              </button>
              <div className="h4 mb-0">{new Date().toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
              <div className="text-white-50">{new Date().toLocaleTimeString('id-ID')}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-3 col-md-6 mb-4">
          <div className="card stat-card stat-card-primary">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-white-50 mb-1">Total Anggota</h6>
                  <h3 className="text-white mb-0">{stats.totalAnggota}</h3>
                  <small className="text-white-50">
                    <i className="fas fa-user-check mr-1"></i>
                    {stats.anggotaAktif} Aktif
                  </small>
                </div>
                <div className="stat-icon">
                  <i className="fas fa-users"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <div className="card stat-card stat-card-success">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-white-50 mb-1">Total Simpanan</h6>
                  <h3 className="text-white mb-0">{formatRupiah(stats.totalSimpanan)}</h3>
                  <small className="text-white-50">
                    <i className="fas fa-arrow-up mr-1"></i>
                    Aset Koperasi
                  </small>
                </div>
                <div className="stat-icon">
                  <i className="fas fa-piggy-bank"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <div className="card stat-card stat-card-warning">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-white-50 mb-1">Total Pinjaman</h6>
                  <h3 className="text-white mb-0">{formatRupiah(stats.totalPinjaman)}</h3>
                  <small className="text-white-50">
                    <i className="fas fa-hand-holding-usd mr-1"></i>
                    {stats.pinjamanAktif} Aktif
                  </small>
                </div>
                <div className="stat-icon">
                  <i className="fas fa-hand-holding-usd"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 mb-4">
          <div className="card stat-card stat-card-danger">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-white-50 mb-1">Sisa Pinjaman</h6>
                  <h3 className="text-white mb-0">{formatRupiah(stats.sisaPinjaman)}</h3>
                  <small className="text-white-50">
                    <i className="fas fa-chart-line mr-1"></i>
                    Outstanding
                  </small>
                </div>
                <div className="stat-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Uang Pangkal Widget */}
      {uangPangkalBelumLunas.length > 0 && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card glass-card">
              <div className="card-header bg-warning text-white d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="fas fa-hand-holding-usd mr-2"></i>
                  Uang Pangkal Belum Lunas
                </h6>
                <span className="badge badge-light">
                  {uangPangkalBelumLunas.length} anggota
                </span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th>No</th>
                        <th>Nama</th>
                        <th>NBA</th>
                        <th>Total</th>
                        <th>Dibayar</th>
                        <th>Sisa</th>
                        <th>Cicilan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uangPangkalBelumLunas.map((item, i) => (
                        <tr key={item.NBA}>
                          <td>{i + 1}</td>
                          <td>{item.Nama}</td>
                          <td>{item.NBA}</td>
                          <td>{formatRupiah(item.Total)}</td>
                          <td>{formatRupiah(item.Dibayar)}</td>
                          <td className="text-danger font-weight-bold">{formatRupiah(item.Sisa)}</td>
                          <td>
                            <span className={`badge ${item.CicilanKe === item.JumlahCicilan ? 'badge-success' : 'badge-warning'}`}>
                              {item.CicilanKe}/{item.JumlahCicilan}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-lg-8 mb-4">
          <div className="card glass-card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-exchange-alt mr-2"></i>
                Transaksi Hari Ini
              </h5>
            </div>
            <div className="card-body">
              {todayTransactions.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-inbox fa-3x mb-3"></i>
                  <p>Belum ada transaksi hari ini</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>No. Jurnal</th>
                        <th>Tanggal</th>
                        <th>Jenis</th>
                        <th>Keterangan</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayTransactions.map((journal) => (
                        <tr key={journal.NoJurnal}>
                          <td>{journal.NoJurnal}</td>
                          <td>{formatDate(journal.TglJurnal)}</td>
                          <td>
                            <span className="badge badge-info">{journal.JenisJurnal}</span>
                          </td>
                          <td>{journal.Keterangan}</td>
                          <td className="text-right font-weight-bold">
                            {formatRupiah(journal.TotalDebet)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-4 mb-4">
          <div className="card glass-card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-bullhorn mr-2"></i>
                Informasi Penting
              </h5>
            </div>
            <div className="card-body">
              {/* Jatuh Tempo Warnings */}
              {(jatuhTempo.summary.overdue > 0 || jatuhTempo.summary.dueToday > 0 || jatuhTempo.summary.dueSoon > 0) && (
                <div className="alert alert-warning mb-3">
                  <h6 className="alert-heading">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    Peringatan Jatuh Tempo
                  </h6>
                  <div className="small">
                    {jatuhTempo.summary.overdue > 0 && (
                      <div className="mb-1">
                        <span className="badge badge-danger mr-1">{jatuhTempo.summary.overdue}</span>
                        <span>Rekening Lewat Jatuh Tempo</span>
                      </div>
                    )}
                    {jatuhTempo.summary.dueToday > 0 && (
                      <div className="mb-1">
                        <span className="badge badge-warning mr-1">{jatuhTempo.summary.dueToday}</span>
                        <span>Rekening Jatuh Tempo Hari Ini</span>
                      </div>
                    )}
                    {jatuhTempo.summary.dueSoon > 0 && (
                      <div className="mb-1">
                        <span className="badge badge-info mr-1">{jatuhTempo.summary.dueSoon}</span>
                        <span>Rekening Akan Jatuh Tempo (7 hari)</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="alert alert-success mb-3">
                <h6 className="alert-heading">
                  <i className="fas fa-chart-bar mr-2"></i>
                  Transaksi Hari Ini
                </h6>
                <p className="mb-0">
                  Total: <strong>{formatRupiah(stats.transaksiHarian)}</strong>
                </p>
              </div>

              {jatuhTempo.overdue.length > 0 && (
                <div className="alert alert-danger mb-0">
                  <h6 className="alert-heading">
                    <i className="fas fa-clock mr-2"></i>
                    Rekening Lewat Jatuh Tempo
                  </h6>
                  <ul className="mb-0 pl-3 small">
                    {jatuhTempo.overdue.slice(0, 5).map((item, idx) => (
                      <li key={idx}>{item.NoRekening} - {item.Nama}</li>
                    ))}
                    {jatuhTempo.overdue.length > 5 && (
                      <li className="text-muted">dan {jatuhTempo.overdue.length - 5} lainnya...</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <div className="card glass-card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-file-alt mr-2"></i>
                Pinjaman Terbaru
              </h5>
            </div>
            <div className="card-body">
              {recentLoans.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-inbox fa-3x mb-3"></i>
                  <p>Belum ada data pinjaman</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>No. Pinjaman</th>
                        <th>NBA</th>
                        <th>Nama</th>
                        <th>Plafond</th>
                        <th>Bunga</th>
                        <th>Status</th>
                        <th>Tgl. Pengajuan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentLoans.map((loan) => (
                        <tr key={loan.NoPinjaman}>
                          <td>{loan.NoPinjaman}</td>
                          <td>{loan.NBA}</td>
                          <td>{loan.NamaAnggota}</td>
                          <td>{formatRupiah(loan.Plafond)}</td>
                          <td>{loan.Bunga}%</td>
                          <td>
                            <span className={`badge badge-${loan.Status === 'Aktif' ? 'success' : loan.Status === 'Pengajuan' ? 'warning' : 'secondary'}`}>
                              {loan.Status}
                            </span>
                          </td>
                          <td>{formatDate(loan.TglPengajuan)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
